import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Search, Gift } from 'lucide-react';
import { useTranslation } from '../context/TranslationContext';
import { SAMPLE_FILES, SAMPLE_SONGS, sampleCopy } from '../data/sampleFiles';
import { useSampleFiles } from '../hooks/useSampleFiles';

function fill(str, map) {
  var out = str;
  Object.keys(map).forEach(function(k) {
    out = out.split('{' + k + '}').join(map[k]);
  });
  return out;
}

// Gold "Try a free sample" card. `variant="full"` is the big version used on
// the /forscore and /mobilesheets pages; `variant="compact"` is the slim block
// shown on each Real Book product page.
export const SampleDownloadCard = function(props) {
  var platform = props.platform === 'mobilesheets' ? 'mobilesheets' : 'forscore';
  var variant = props.variant || 'full';
  var { language } = useTranslation();
  var tx = sampleCopy(language);
  var file = SAMPLE_FILES[platform];
  var sampleUrl = useSampleFiles()(platform).url;
  var [showSongs, setShowSongs] = useState(false);
  var steps = platform === 'mobilesheets' ? tx.mobilesheetsSteps : tx.forscoreSteps;
  var vars = { app: file.appName, ext: file.ext };

  var downloadBtn = (
    <a
      href={sampleUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={'sample-download-' + platform + '-' + variant}
      className="inline-flex items-center gap-2 bg-[#d4af37] hover:bg-white text-black font-semibold text-sm rounded-full px-6 py-3 transition-colors"
    >
      <Download className="w-4 h-4" />
      {fill(tx.download, vars)}
    </a>
  );

  if (variant === 'compact') {
    return (
      <div
        data-testid={'sample-card-compact-' + platform}
        className="border border-[#d4af37]/30 bg-[#d4af37]/[0.06] rounded-2xl p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-4 h-4 text-[#d4af37]" />
          <span className="text-[#d4af37] text-[11px] font-semibold uppercase tracking-[0.16em]">{tx.kicker}</span>
        </div>
        <h3 className="text-white text-base md:text-lg font-semibold mb-1">{tx.compactTitle}</h3>
        <p className="text-neutral-400 text-sm leading-relaxed mb-4">{fill(tx.compactBody, vars)}</p>
        {downloadBtn}
        <div className="mt-3">
          <Link
            to={platform === 'mobilesheets' ? '/mobilesheets' : '/forscore'}
            data-testid={'sample-see-more-' + platform}
            className="text-[#d4af37] text-xs hover:text-white transition-colors"
          >
            {tx.seeMore} →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={'sample-card-' + platform}
      id="free-sample"
      className="relative overflow-hidden border border-[#d4af37]/35 bg-gradient-to-br from-[#d4af37]/[0.13] to-transparent rounded-3xl p-7 md:p-10 mb-14"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-5 h-5 text-[#d4af37]" />
        <span className="text-[#d4af37] text-[11px] font-semibold uppercase tracking-[0.18em]">{tx.kicker}</span>
      </div>

      <h2 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-4 max-w-2xl">{tx.title}</h2>
      <p className="text-neutral-300 text-sm md:text-base leading-relaxed max-w-2xl mb-7">{fill(tx.subtitle, vars)}</p>

      {downloadBtn}

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        <div>
          <h3 className="text-white text-base md:text-lg font-semibold mb-3">{tx.howTitle}</h3>
          <ol className="space-y-2">
            {steps.map(function(s, i) {
              return (
                <li key={i} className="flex gap-3 text-neutral-400 text-sm leading-relaxed">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#d4af37]/20 text-[#d4af37] text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              );
            })}
          </ol>
        </div>

        <div>
          <h3 className="text-white text-base md:text-lg font-semibold mb-3 flex items-center gap-2">
            <Search className="w-4 h-4 text-[#d4af37]" />
            {tx.songsTitle}
          </h3>
          <button
            onClick={function() { setShowSongs(!showSongs); }}
            data-testid={'sample-songs-toggle-' + platform}
            className="text-[#d4af37] text-xs hover:text-white transition-colors mb-3"
          >
            {showSongs ? tx.hideSongs : tx.showSongs} {showSongs ? '↑' : '↓'}
          </button>
          {showSongs ? (
            <ol
              data-testid={'sample-songs-list-' + platform}
              className="grid sm:grid-cols-2 gap-x-6 gap-y-1 list-decimal list-inside"
            >
              {SAMPLE_SONGS.map(function(s) {
                return <li key={s} className="text-neutral-400 text-xs leading-relaxed">{s}</li>;
              })}
            </ol>
          ) : (
            <p className="text-neutral-500 text-xs leading-relaxed">
              {SAMPLE_SONGS.slice(0, 5).join(' · ')} …
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SampleDownloadCard;
